import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, X, Clock } from 'lucide-react';

export default function DemoBanner() {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState('');
  const [demoSession, setDemoSession] = useState(null);
  const bannerRef = useRef(null);

  useEffect(() => {
    const expires = localStorage.getItem('demo_expires_at');
    const brand = localStorage.getItem('demo_brand');
    if (expires && brand) {
      setDemoSession({ expires_at: expires, brand });
    }
  }, []);

  // Misst Banner-Höhe dynamisch und setzt CSS-Variable für DashboardLayout
  useEffect(() => {
    const root = document.documentElement;
    if (!demoSession) {
      root.style.setProperty('--demo-banner-height', '0px');
      return;
    }

    const updateHeight = () => {
      if (bannerRef.current) {
        const h = bannerRef.current.getBoundingClientRect().height;
        root.style.setProperty('--demo-banner-height', `${Math.ceil(h)}px`);
      }
    };

    // Initial measurement after paint
    requestAnimationFrame(updateHeight);

    const observer = new ResizeObserver(updateHeight);
    if (bannerRef.current) observer.observe(bannerRef.current);

    return () => {
      observer.disconnect();
      root.style.setProperty('--demo-banner-height', '0px');
    };
  }, [demoSession]);

  // Countdown timer
  useEffect(() => {
    if (!demoSession?.expires_at) return;
    const tick = () => {
      const diff = new Date(demoSession.expires_at) - new Date();
      if (diff <= 0) { setTimeLeft('Abgelaufen'); return; }
      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${mins}:${secs.toString().padStart(2, '0')}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [demoSession]);

  const handleExit = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('demo_access_token');
    localStorage.removeItem('demo_expires_at');
    localStorage.removeItem('demo_brand');
    localStorage.removeItem('demo_session_id');
    localStorage.removeItem('is_demo');
    setDemoSession(null);
    window.location.replace('/');
  };

  if (!demoSession) return null;

  return (
    <div
      ref={bannerRef}
      data-testid="demo-banner"
      className="fixed top-0 left-0 right-0 z-[9999] bg-orange-500 text-white px-4 py-2 flex items-center justify-between shadow-lg"
      style={{ paddingTop: 'calc(8px + env(safe-area-inset-top))' }}
    >
      <div className="flex items-center gap-2">
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="font-bold text-sm tracking-wide">DEMO-MODUS</span>
        <span className="hidden sm:inline text-sm opacity-90">
          – Sandbox-Umgebung. Keine echten Daten.
        </span>
      </div>

      <div className="flex items-center gap-3">
        {timeLeft && (
          <div className="flex items-center gap-1 bg-orange-600 rounded-full px-3 py-0.5">
            <Clock className="w-3 h-3" />
            <span className="text-xs font-mono font-bold" data-testid="demo-timer">{timeLeft}</span>
          </div>
        )}
        <button
          onClick={handleExit}
          data-testid="demo-exit-button"
          className="flex items-center gap-1 bg-orange-700 hover:bg-orange-800 rounded-lg px-3 py-1 text-xs font-semibold transition-colors"
        >
          <X className="w-3 h-3" />
          <span className="hidden sm:inline">Demo beenden</span>
        </button>
      </div>
    </div>
  );
}
